import { AxiosResponse } from 'axios'
import ApiService from '@/plugins/api-plugin/services/api.service'
import { PageNumberPagination } from '@/interfaces/common/PageNumberPagination'
import {
    AdjustStockBody,
    AdjustStockDao,
    ArchiveCheckDao,
    CreateExplorerViewBody,
    ExplorerExportBody,
    ExplorerViewDao,
    UpdateExplorerViewBody,
    ExplorerCountsBody,
    ExplorerCountsDao,
    ExportBreakdownDao,
    ExplorerPageMeta,
    ExplorerRowDaos,
    ExplorerSearchBody,
    ExplorerSelectionBody,
    ExplorerSelectionDao,
    ExplorerTabKey,
    TransferStockBody,
    TransferStockDao,
    TransferTargetDao,
} from '@/interfaces/materials-management/Explorer.dao'
import {
    CategoriesLookupDao,
    LookupDao,
    LookupQuery,
    MatchSkusDao,
    MaterialsLookupKind,
    NewSuggestionDao,
    SkuTakenDao,
    StockAttributesLookupDao,
} from '@/interfaces/materials-management/MaterialsLookup.dao'
import {
    InventoryMovementsDao,
    InventoryMovementsQuery,
    InventorySummaryDao,
    MaterialChangeLogJobDao,
    MaterialDetailDao,
    MaterialReadinessDao,
} from '@/interfaces/materials-management/MaterialDetail.dao'
import { BundleDetailDao } from '@/interfaces/materials-management/BundleDetail.dao'
import {
    ProfileDetailDao,
    ProfileMappingDao,
    ProfileMappingsBody,
    ProfilesPageDao,
    ProfilesSearchBody,
} from '@/interfaces/materials-management/Profiles.dao'
import { OverviewDao } from '@/interfaces/materials-management/Overview.dao'

/** The Materials Management Explorer (`/materials-management`, role `Web_Nav_Inventory_MaterialsManagement`). */
export default class MaterialsManagementService extends ApiService {
    /** GET /materials-management/lookups/{kind} - filter options in the user's scope; `ids` restores chosen ones. */
    async getLookup<T extends LookupDao = LookupDao>(
        kind: MaterialsLookupKind,
        query: LookupQuery,
    ): Promise<[T[], PageNumberPagination] | null> {
        return this.request<undefined, [T[], PageNumberPagination]>(
            'GET',
            `/materials-management/lookups/${kind}`,
            { ...query },
            undefined,
            undefined,
            'getMaterialsManagementLookup',
        ).catch(() => null)
    }

    /** POST /materials-management/skus/match - the pasted SKUs nothing in scope carries; the list travels in the body. */
    async matchSkus(skus: string[]): Promise<MatchSkusDao | null> {
        return this.request<{ skus: string[] }, MatchSkusDao>(
            'POST',
            '/materials-management/skus/match',
            undefined,
            { skus },
            undefined,
            'matchMaterialsManagementSkus',
        ).catch(() => null)
    }

    /**
     * POST the search of an Explorer tab; Stock reads the Manage Inventory API (`/inventory/stock/search`).
     * A failure rejects without an alert: the table shows the server's refusal, such as a too broad search.
     */
    async searchTab<K extends ExplorerTabKey>(
        tab: K,
        body: ExplorerSearchBody,
    ): Promise<[ExplorerRowDaos[K][], ExplorerPageMeta] | null> {
        return this.request<ExplorerSearchBody, [ExplorerRowDaos[K][], ExplorerPageMeta]>(
            'POST',
            tab === 'stock' ? '/inventory/stock/search' : `/materials-management/${tab}/search`,
            undefined,
            body,
            undefined,
            'searchMaterialsManagementTab',
            { suppressErrorAlert: true },
        )
    }

    /** POST /materials-management/explorer/counts - tab totals and quick filter counts; a scoped view only, no alert on failure. */
    async getExplorerCounts(body: ExplorerCountsBody): Promise<ExplorerCountsDao | null> {
        return this.request<ExplorerCountsBody, ExplorerCountsDao>(
            'POST',
            '/materials-management/explorer/counts',
            undefined,
            body,
            undefined,
            'getMaterialsManagementExplorerCounts',
            { suppressErrorAlert: true },
        ).catch(() => null)
    }

    /** GET /materials-management/listings/{priceId}/export-breakdown - the steps of the quantity a price row sends. */
    async getExportBreakdown(priceId: number): Promise<ExportBreakdownDao | null> {
        return this.request<undefined, ExportBreakdownDao>(
            'GET',
            `/materials-management/listings/${priceId}/export-breakdown`,
            undefined,
            undefined,
            undefined,
            'getMaterialsManagementExportBreakdown',
        ).catch(() => null)
    }

    private categories: Promise<CategoriesLookupDao | null> | null = null

    /** GET /materials-management/lookups/categories - both category lists a price row takes, read once per session. */
    async getCategories(): Promise<CategoriesLookupDao | null> {
        this.categories ??= this.request<undefined, CategoriesLookupDao>(
            'GET',
            '/materials-management/lookups/categories',
            undefined,
            undefined,
            undefined,
            'getMaterialsManagementCategories',
        ).catch(() => null)
        const categories = await this.categories
        if (!categories) this.categories = null
        return categories
    }

    private stockAttributes: Promise<StockAttributesLookupDao | null> | null = null

    /** GET /materials-management/lookups/stock-attributes - the tax classes and countries a stock SKU takes, read once per session. */
    async getStockAttributes(): Promise<StockAttributesLookupDao | null> {
        this.stockAttributes ??= this.request<undefined, StockAttributesLookupDao>(
            'GET',
            '/materials-management/lookups/stock-attributes',
            undefined,
            undefined,
            undefined,
            'getMaterialsManagementStockAttributes',
        ).catch(() => null)
        const lookup = await this.stockAttributes
        if (!lookup) this.stockAttributes = null
        return lookup
    }

    /** POST /materials-management/{listings|bundles}/selection - the price rows a bulk dialog checks and writes. */
    async getSelection(tab: 'listings' | 'bundles', body: ExplorerSelectionBody): Promise<ExplorerSelectionDao> {
        return this.request<ExplorerSelectionBody, ExplorerSelectionDao>(
            'POST',
            `/materials-management/${tab}/selection`,
            undefined,
            body,
            undefined,
            'getMaterialsManagementSelection',
            { suppressErrorAlert: true },
        )
    }

    /** POST /materials-management/{materials|listings|bundles}/archive-check - what blocks, drags and warns per item. */
    async archiveCheck(
        tab: 'materials' | 'listings' | 'bundles',
        body: ExplorerSelectionBody,
    ): Promise<ArchiveCheckDao> {
        return this.request<ExplorerSelectionBody, ArchiveCheckDao>(
            'POST',
            `/materials-management/${tab}/archive-check`,
            undefined,
            body,
            undefined,
            'checkMaterialsManagementArchive',
            { suppressErrorAlert: true },
        )
    }

    /** POST /inventory/stock/adjust - sets a new On hand and allocates open back orders at the location. */
    async adjustStock(body: AdjustStockBody): Promise<AdjustStockDao> {
        return this.request<AdjustStockBody, AdjustStockDao>(
            'POST',
            '/inventory/stock/adjust',
            undefined,
            body,
            undefined,
            'adjustMaterialsManagementStock',
            { suppressErrorAlert: true },
        )
    }

    /** POST /inventory/stock/transfer - moves units to another location and allocates there. */
    async transferStock(body: TransferStockBody): Promise<TransferStockDao> {
        return this.request<TransferStockBody, TransferStockDao>(
            'POST',
            '/inventory/stock/transfer',
            undefined,
            body,
            undefined,
            'transferMaterialsManagementStock',
            { suppressErrorAlert: true },
        )
    }

    /** GET /materials-management/views - the Team views and the user's own, of every tab. */
    async getViews(): Promise<ExplorerViewDao[] | null> {
        return this.request<undefined, ExplorerViewDao[]>(
            'GET',
            '/materials-management/views',
            undefined,
            undefined,
            undefined,
            'getMaterialsManagementViews',
            { suppressErrorAlert: true },
        ).catch(() => null)
    }

    /** POST /materials-management/views - saves the state of a tab as a view; a taken name is a 409. */
    async createView(body: CreateExplorerViewBody): Promise<ExplorerViewDao> {
        return this.request<CreateExplorerViewBody, ExplorerViewDao>(
            'POST',
            '/materials-management/views',
            undefined,
            body,
            undefined,
            'createMaterialsManagementView',
            { suppressErrorAlert: true },
        )
    }

    /** PATCH /materials-management/views/{id} - renames, shares or overwrites a view the user saved. */
    async updateView(id: number, body: UpdateExplorerViewBody): Promise<ExplorerViewDao> {
        return this.request<UpdateExplorerViewBody, ExplorerViewDao>(
            'PATCH',
            `/materials-management/views/${id}`,
            undefined,
            body,
            undefined,
            'updateMaterialsManagementView',
            { suppressErrorAlert: true },
        )
    }

    /** DELETE /materials-management/views/{id} - deletes a view the user saved. */
    async deleteView(id: number): Promise<void> {
        await this.request<undefined, null>(
            'DELETE',
            `/materials-management/views/${id}`,
            undefined,
            undefined,
            undefined,
            'deleteMaterialsManagementView',
            { suppressErrorAlert: true },
        )
    }

    /** POST /materials-management/{tab}/export - the rows of a tab as xlsx, up to 50,000; errors arrive as a Blob. */
    async exportTab(tab: ExplorerTabKey, body: ExplorerExportBody): Promise<AxiosResponse<Blob>> {
        return this.request<ExplorerExportBody, AxiosResponse<Blob>>(
            'POST',
            `/materials-management/${tab}/export`,
            undefined,
            body,
            'blob',
            'exportMaterialsManagementTab',
            { suppressErrorAlert: true },
        )
    }

    /** GET /materials-management/new/suggest - active stock parents or bundle components of a client, top 20. */
    async suggestNew(kind: 'stock' | 'listing', clientId: number, q: string): Promise<NewSuggestionDao[]> {
        return this.request<undefined, NewSuggestionDao[]>(
            'GET',
            '/materials-management/new/suggest',
            { kind, clientId, q },
            undefined,
            undefined,
            'suggestMaterialsManagementNew',
            { suppressErrorAlert: true },
        )
    }

    /** GET /materials-management/new/sku-taken - whether a material of the client already carries the SKU. */
    async skuTaken(clientId: number, sku: string): Promise<SkuTakenDao> {
        return this.request<undefined, SkuTakenDao>(
            'GET',
            '/materials-management/new/sku-taken',
            { clientId, sku },
            undefined,
            undefined,
            'checkMaterialsManagementSkuTaken',
            { suppressErrorAlert: true },
        )
    }

    /** GET /inventory/stock/transfer-targets - the movable locations of the stock SKU's Op Centres. */
    async getTransferTargets(materialId: number, fromLocationId: number): Promise<TransferTargetDao[]> {
        return this.request<undefined, TransferTargetDao[]>(
            'GET',
            '/inventory/stock/transfer-targets',
            { materialId, fromLocationId },
            undefined,
            undefined,
            'getMaterialsManagementTransferTargets',
            { suppressErrorAlert: true },
        )
    }

    /** GET /materials-management/materials/{id} - the Material page header and Overview fields; a 404 outside the scope. */
    async getMaterial(id: number): Promise<MaterialDetailDao> {
        return this.request<undefined, MaterialDetailDao>(
            'GET',
            `/materials-management/materials/${id}`,
            undefined,
            undefined,
            undefined,
            'getMaterialsManagementMaterial',
            { suppressErrorAlert: true },
        )
    }

    /** GET /materials-management/bundles/{id} - the Bundle page: export, components, splits and price rows; a 404 outside the scope. */
    async getBundle(id: number): Promise<BundleDetailDao> {
        return this.request<undefined, BundleDetailDao>(
            'GET',
            `/materials-management/bundles/${id}`,
            undefined,
            undefined,
            undefined,
            'getMaterialsManagementBundle',
            { suppressErrorAlert: true },
        )
    }

    /** GET /materials-management/bundles/{id}/change-log - the jobs that changed the bundle, 12 months. */
    async getBundleChangeLog(
        id: number,
        query: { page?: number; limit?: number },
    ): Promise<[MaterialChangeLogJobDao[], PageNumberPagination] | null> {
        return this.request<undefined, [MaterialChangeLogJobDao[], PageNumberPagination]>(
            'GET',
            `/materials-management/bundles/${id}/change-log`,
            { ...query },
            undefined,
            undefined,
            'getMaterialsManagementBundleChangeLog',
        ).catch(() => null)
    }

    /** GET /materials-management/materials/{id}/readiness - every readiness step per listing and channel account; a 404 outside the scope. */
    async getMaterialReadiness(id: number): Promise<MaterialReadinessDao> {
        return this.request<undefined, MaterialReadinessDao>(
            'GET',
            `/materials-management/materials/${id}/readiness`,
            undefined,
            undefined,
            undefined,
            'getMaterialsManagementMaterialReadiness',
            { suppressErrorAlert: true },
        )
    }

    /** GET /materials-management/materials/{id}/change-log - the jobs that changed the stock SKU or its listings, 12 months. */
    async getMaterialChangeLog(
        id: number,
        query: { page?: number; limit?: number },
    ): Promise<[MaterialChangeLogJobDao[], PageNumberPagination] | null> {
        return this.request<undefined, [MaterialChangeLogJobDao[], PageNumberPagination]>(
            'GET',
            `/materials-management/materials/${id}/change-log`,
            { ...query },
            undefined,
            undefined,
            'getMaterialsManagementMaterialChangeLog',
        ).catch(() => null)
    }

    /** GET /inventory/materials/{id}/summaries - the stock of a stock SKU per location, scoped by Op Centre; a 404 outside the scope. */
    async getMaterialSummaries(id: number): Promise<InventorySummaryDao[]> {
        return this.request<undefined, InventorySummaryDao[]>(
            'GET',
            `/inventory/materials/${id}/summaries`,
            undefined,
            undefined,
            undefined,
            'getMaterialsManagementMaterialSummaries',
            { suppressErrorAlert: true },
        )
    }

    /** GET /inventory/materials/{id}/movements - one page of movements per stock location, with balances. */
    async getMaterialMovements(id: number, query: InventoryMovementsQuery): Promise<InventoryMovementsDao | null> {
        return this.request<undefined, InventoryMovementsDao>(
            'GET',
            `/inventory/materials/${id}/movements`,
            { ...query },
            undefined,
            undefined,
            'getMaterialsManagementMaterialMovements',
        ).catch(() => null)
    }

    /** GET /materials-management/overview - the latest health snapshot summed over the user's scope and the chosen clients. */
    async getOverview(clientIds: number[]): Promise<OverviewDao> {
        return this.request<undefined, OverviewDao>(
            'GET',
            '/materials-management/overview',
            clientIds.length ? { clientIds: clientIds.join(',') } : undefined,
            undefined,
            undefined,
            'getMaterialsManagementOverview',
            { suppressErrorAlert: true },
        )
    }

    /** POST /materials-management/profiles/search - the Mappings page: profiles in scope with their checks and the check counts. */
    async searchProfiles(body: ProfilesSearchBody): Promise<ProfilesPageDao> {
        return this.request<ProfilesSearchBody, ProfilesPageDao>(
            'POST',
            '/materials-management/profiles/search',
            undefined,
            body,
            undefined,
            'searchMaterialsManagementProfiles',
            { suppressErrorAlert: true },
        )
    }

    /**
     * GET /materials-management/profiles/{id} - the channel profile page; a 404 outside the scope.
     * `live` reads Stock will not flow now instead of from the shared pass.
     */
    async getProfile(id: number, live = false): Promise<ProfileDetailDao> {
        return this.request<undefined, ProfileDetailDao>(
            'GET',
            `/materials-management/profiles/${id}`,
            live ? { live: true } : undefined,
            undefined,
            undefined,
            'getMaterialsManagementProfile',
            { suppressErrorAlert: true },
        )
    }

    /** POST /materials-management/profiles/{id}/mappings - one page of the listings and bundles a profile maps. */
    async getProfileMappings(
        id: number,
        body: ProfileMappingsBody,
    ): Promise<[ProfileMappingDao[], PageNumberPagination]> {
        return this.request<ProfileMappingsBody, [ProfileMappingDao[], PageNumberPagination]>(
            'POST',
            `/materials-management/profiles/${id}/mappings`,
            undefined,
            body,
            undefined,
            'getMaterialsManagementProfileMappings',
            { suppressErrorAlert: true },
        )
    }
}
